import { useEffect } from 'react';

// Defined in vite.config.js
declare const __APP_VERSION__: string;

/**
 * Detecta si hay una versión más nueva desplegada. YA NO recarga la página.
 *
 * El service worker (vite-plugin-pwa, autoUpdate) ya no precachea index.html
 * ni el JS, y `navigateFallback` está desactivado: el shell de la app viene
 * siempre fresco de red, así que cada arranque carga el JS actual. La recarga
 * forzada que había aquí era justo lo que dejaba la app en blanco al entrar
 * ("no arranca hasta que le doy a actualizar"): el JS cacheado tenía un
 * __APP_VERSION__ viejo, esto veía el version.json nuevo y recargaba en pleno
 * arranque. Con el shell siempre fresco no hace falta forzar nada.
 *
 * Se mantiene solo la detección para dejar traza; quien tenga la pestaña
 * abierta desde antes de un deploy recibirá la versión nueva en su próxima
 * navegación, sin interrupción.
 */
export const useVersionCheck = (checkInterval: number = 60000) => {
    useEffect(() => {
        if (import.meta.env.DEV) return;

        let cancelled = false;

        const checkVersion = async () => {
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`);
                if (!response.ok || cancelled) return;
                const data = await response.json();
                if (data.version && data.version !== __APP_VERSION__) {
                    console.info(
                        `[AutoUpdate] Hay una versión más reciente (servidor: ${data.version}, ` +
                        `local: ${__APP_VERSION__}). Se cargará en la próxima navegación.`
                    );
                }
            } catch {
                // Sin conexión o version.json no disponible: se ignora en silencio.
            }
        };

        checkVersion();
        const interval = setInterval(checkVersion, checkInterval);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [checkInterval]);

    return {};
};
